import { INewsContentService } from '../services/i-news-content-service';
import { Language } from '../dto/language';
import { TranslationMode } from '../dto/translation-mode';
import { CategoryDto, NewsCategoryRequest } from '../dto/categories';
import { MakeReactive, noopMakeReactive } from '../ui/make-reactive';

interface CategoryCacheEntry {
	categories: CategoryDto[];
	userCategories?: CategoryDto[];
	timestamp: number;
}

interface NewsCategoryStoreState {
	cache: Map<string, CategoryCacheEntry>;
	isLoading: boolean;
	error: string;
}

/**
 * Cache TTL: 5 minutes
 */
const CACHE_TTL = 5 * 60 * 1000;

/**
 * Store for managing news categories with caching
 */
export class NewsCategoryStore {
	private state: NewsCategoryStoreState;
	private service: INewsContentService;

	constructor(
		service: INewsContentService,
		makeReactive: MakeReactive<unknown> = noopMakeReactive
	) {
		this.service = service;
		this.state = makeReactive({
			cache: new Map<string, CategoryCacheEntry>(),
			isLoading: false,
			error: ''
		}) as NewsCategoryStoreState;
	}

	private createCacheKey(sourceLang: Language, targetLang: Language, mode: TranslationMode, userId?: string): string {
		// Categories depend on authentication (custom categories), so cache must be user-specific.
		return `${sourceLang}_${targetLang}_${mode}_${userId ?? 'anon'}`;
	}

	private isCacheValid(entry: CategoryCacheEntry): boolean {
		return Date.now() - entry.timestamp < CACHE_TTL;
	}

	/**
	 * Get news categories with automatic caching
	 */
	async getNewsCategories(
		sourceLang: Language,
		targetLang: Language,
		mode: TranslationMode,
		userId?: string
	): Promise<{ categories: CategoryDto[]; userCategories?: CategoryDto[] }> {
		const cacheKey = this.createCacheKey(sourceLang, targetLang, mode, userId);

		// Check cache first
		const cached = this.state.cache.get(cacheKey);
		if (cached && this.isCacheValid(cached)) {
			return {
				categories: cached.categories,
				userCategories: cached.userCategories
			};
		}

		// Fetch from service
		try {
			this.state.isLoading = true;
			this.state.error = '';

			const request: NewsCategoryRequest = {
				sourceLang,
				targetLang,
				mode
			};

			const response = await this.service.getCategories(request, userId);

			// Update cache
			const entry: CategoryCacheEntry = {
				categories: response.categories,
				userCategories: response.userCategories,
				timestamp: Date.now()
			};
			this.state.cache.set(cacheKey, entry);

			return {
				categories: response.categories,
				userCategories: response.userCategories
			};

		} catch (error) {
			const errorMessage = error instanceof Error ? error.message : 'Unknown error loading categories';
			this.state.error = errorMessage;
			throw error;
		} finally {
			this.state.isLoading = false;
		}
	}

	/**
	 * Clear cache for specific language/mode combination
	 */
	clearCache(sourceLang: Language, targetLang: Language, mode: TranslationMode, userId?: string): void {
		const cacheKey = this.createCacheKey(sourceLang, targetLang, mode, userId);
		this.state.cache.delete(cacheKey);
	}

	/**
	 * Clear all cache
	 */
	clearAllCache(): void {
		this.state.cache.clear();
	}

	/**
	 * Check if currently loading
	 */
	isLoading(): boolean {
		return this.state.isLoading;
	}

	/**
	 * Get last error
	 */
	getError(): string {
		return this.state.error;
	}
}



